import MuiCheckIcon from '@mui/icons-material/Check';
import MuiErrorIcon from '@mui/icons-material/Error';
import MuiCancelIcon from '@mui/icons-material/Cancel';
import MuiWarningIcon from '@mui/icons-material/Warning';
import MuiSecurityIcon from '@mui/icons-material/Security';
import MuiSettingsIcon from '@mui/icons-material/Settings';
import MuiCheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Stack, alpha, Button, Typography } from '@mui/material';
import MuiLocationDisabledIcon from '@mui/icons-material/LocationDisabled';

import MuiIcon, { type MuiIconComponent } from 'src/components/mui-icon';

import { PermissionState, DevicePermission } from './types';

// ----------------------------------------------------------------------

interface PermissionItemProps {
  permission: DevicePermission;
  onGrant: () => void;
  onOpenSettings: () => void;
  onOpenLocationSettings: () => void;
  isLoading: boolean;
}

// Get status badge configuration
function getStatusConfig(status: PermissionState): {
  label: string;
  color: 'success' | 'error' | 'warning' | 'info';
  icon: MuiIconComponent;
} {
  switch (status) {
    case 'granted':
      return { label: 'Granted', color: 'success', icon: MuiCheckCircleIcon };
    case 'denied':
      return { label: 'Denied', color: 'error', icon: MuiCancelIcon };
    case 'prompt-with-rationale':
      return { label: 'Required', color: 'warning', icon: MuiErrorIcon };
    case 'limited':
      return { label: 'Limited', color: 'info', icon: MuiWarningIcon };
    case 'settings-disabled':
      return { label: 'Location Off', color: 'warning', icon: MuiLocationDisabledIcon };
    default:
      return { label: 'Required', color: 'warning', icon: MuiErrorIcon };
  }
}

// Get border color based on permission status
function getBorderColor(
  isGranted: boolean,
  isDenied: boolean,
  isSettingsDisabled: boolean
): string {
  if (isGranted) return 'success.main';
  if (isDenied) return 'error.light';
  if (isSettingsDisabled) return 'warning.main';
  return 'divider';
}

// Get icon background color based on permission status
function getIconBgColor(
  isGranted: boolean,
  isDenied: boolean,
  isSettingsDisabled: boolean
): string {
  if (isGranted) return 'success.lighter';
  if (isDenied) return 'error.lighter';
  if (isSettingsDisabled) return 'warning.lighter';
  return 'grey.100';
}

// Get icon color based on permission status
function getIconColor(isGranted: boolean, isDenied: boolean, isSettingsDisabled: boolean): string {
  if (isGranted) return 'success.main';
  if (isDenied) return 'error.main';
  if (isSettingsDisabled) return 'warning.dark';
  return 'text.secondary';
}

// ----------------------------------------------------------------------

export default function PermissionItem({
  permission,
  onGrant,
  onOpenSettings,
  onOpenLocationSettings,
  isLoading,
}: PermissionItemProps) {
  const statusConfig = getStatusConfig(permission.status);
  const isGranted = permission.status === 'granted';
  const isDenied = permission.status === 'denied';
  const isSettingsDisabled = permission.status === 'settings-disabled';

  // Compute styles using helper functions to avoid nested ternaries
  const borderColor = getBorderColor(isGranted, isDenied, isSettingsDisabled);
  const iconBgColor = getIconBgColor(isGranted, isDenied, isSettingsDisabled);
  const iconColor = getIconColor(isGranted, isDenied, isSettingsDisabled);

  let actionLabel = 'Continue';
  let actionIcon = MuiSecurityIcon;
  let actionColor: 'primary' | 'error' | 'warning' = 'primary';
  let actionVariant: 'contained' | 'outlined' = 'contained';
  let handleAction = onGrant;

  if (isDenied) {
    actionLabel = 'Open Settings';
    actionIcon = MuiSettingsIcon;
    actionColor = 'error';
    actionVariant = 'outlined';
    handleAction = onOpenSettings;
  } else if (isSettingsDisabled) {
    actionLabel = 'Turn On Location';
    actionIcon = MuiLocationDisabledIcon;
    actionColor = 'warning';
    handleAction = onOpenLocationSettings;
  }

  // Compute background color
  const getBackgroundColor = () => {
    if (isGranted)
      return (theme: { palette: { success: { main: string } } }) =>
        alpha(theme.palette.success.main, 0.08);
    if (isDenied)
      return (theme: { palette: { error: { main: string } } }) =>
        alpha(theme.palette.error.main, 0.04);
    if (isSettingsDisabled)
      return (theme: { palette: { warning: { main: string } } }) =>
        alpha(theme.palette.warning.main, 0.06);
    return 'background.paper';
  };

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: 2,
        borderColor,
        backgroundColor: getBackgroundColor(),
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {/* Permission Icon */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: iconBgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MuiIcon
            icon={permission.icon}
            width={32}
            sx={{
              color: iconColor,
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {permission.name}
            </Typography>

            {/* Status Badge */}
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: `${statusConfig.color}.lighter`,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <MuiIcon
                icon={statusConfig.icon}
                width={14}
                sx={{ color: `${statusConfig.color}.main` }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: `${statusConfig.color}.dark` }}
              >
                {statusConfig.label}
              </Typography>
            </Box>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: 1.5,
              mb: 1.5,
            }}
          >
            {permission.description}
          </Typography>

          {/* Action Button */}
          {!isGranted && (
            <Button
              variant={actionVariant}
              color={actionColor}
              size="small"
              onClick={handleAction}
              disabled={isLoading}
              startIcon={<MuiIcon icon={actionIcon} width={18} />}
              sx={{
                fontWeight: 600,
                ...(isDenied && {
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                }),
              }}
            >
              {actionLabel}
            </Button>
          )}

          {isGranted && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <MuiIcon icon={MuiCheckIcon} width={16} sx={{ color: 'success.main' }} />
              <Typography variant="caption" color="success.main" fontWeight={600}>
                Permission granted
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
